import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceMonth, attendanceConfig, createAttProposal, attLockStatus } from '../api/attendance';
import { useModal } from '../components/ModalProvider';
import { useToast } from '../components/Toast';
import FaceAttendanceModal from '../components/FaceAttendanceModal';

const WD = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const colOf = (wd) => (wd === 0 ? 6 : wd - 1); // 0=CN..6=T7 -> cột 0..6 (T2..CN)
const VIOLS = [['late', 'Đi muộn'], ['early', 'Về sớm'], ['miss', 'Quên chấm công']];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Màu chấm trạng thái từng ngày trên lịch (khớp ngữ nghĩa app).
function dots(d, today) {
  if (!d || d.empty) {
    if (d && d.past && d.weekday >= 1 && d.weekday <= 5) return ['var(--danger)']; // ngày làm đã qua, không chấm
    return [];
  }
  const out = [];
  if (d.status === 'work' || d.status === 'half') out.push('var(--ok)');
  else if (d.status === 'absent') out.push('var(--danger)');
  if ((d.phat || 0) > 0) out.push('var(--warn-fill)');
  return out.length ? out : ['var(--ok)'];
}

// Bảng đề xuất/giải trình (bottom-sheet) — gửi đơn cho 1 ngày.
function ProposalSheet({ date, day, types, locked, onClose, onDone }) {
  const toast = useToast();
  const [type, setType] = useState(types[0]?.key || '');
  const [reason, setReason] = useState('');
  const [waives, setWaives] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const dayViols = VIOLS.filter(([k]) => day?.viol?.[k]);
  const d = new Date(`${date}T00:00:00`);
  const toggle = (k) => setWaives((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const submit = async () => {
    setBusy(true);
    try {
      await createAttProposal({ date, type, reason, waives: [...waives] });
      toast('Đã gửi đề xuất, chờ duyệt');
      onDone();
    } catch (e) { toast(e.response?.data?.message || 'Lỗi gửi đề xuất', 'danger'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mbatt-sheet">
      <h3 style={{ marginTop: 0 }}>Chi tiết ngày {pad(d.getDate())}/{pad(d.getMonth() + 1)}</h3>
      {/* CHI TIẾT NGÀY (read-only): đề xuất đã duyệt chưa + kế toán đã chốt công chưa */}
      {day && (!day.empty || day.proposal) && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px', marginBottom: 10, background: 'var(--panel2)' }}>
          {day.proposal && (
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
              {(types.find((t) => t.key === day.proposal.type)?.label || 'Đơn đề xuất')}
              <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: '#fff', background: day.proposal.status === 'approved' ? 'var(--ok)' : day.proposal.status === 'rejected' ? 'var(--danger)' : 'var(--warn-fill)' }}>
                {day.proposal.status === 'approved' ? 'Đã duyệt' : day.proposal.status === 'rejected' ? 'Từ chối' : 'Chờ duyệt'}
              </span>
            </div>
          )}
          <div className="small" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>🕒 {day.checkIn || day.checkOut ? `${day.checkIn || '—'}${day.checkOut ? `–${day.checkOut}` : ''}` : 'chưa chấm'}</span>
            <span>📊 Công: <b>{day.cong != null ? day.cong : '—'}</b></span>
          </div>
          <div className="small" style={{ marginTop: 5, color: day.adminAdjusted ? 'var(--ok)' : 'var(--muted)', fontWeight: day.adminAdjusted ? 700 : 400 }}>
            {day.adminAdjusted ? `✓ Kế toán đã chốt lại (${day.cong ?? 0} công)` : 'Kế toán chưa chốt lại — tính tự động'}
          </div>
        </div>
      )}
      {dayViols.length > 0 && <div className="small" style={{ color: 'var(--warn)', marginBottom: 10 }}>Ngày này đang có lỗi: {dayViols.map(([, l]) => l).join(' · ')}</div>}
      {locked ? (
        <>
          <div className="small" style={{ color: 'var(--muted)', marginBottom: 6 }}>Tháng đã chốt — chỉ xem, không gửi đề xuất được.</div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button className="btn ghost" onClick={onClose}>Đóng</button>
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label>Hình thức</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {type === 'viec_cong_ty' && dayViols.length > 0 && (
            <div className="field">
              <label>Lỗi cần giải trình (duyệt xong chỉ bỏ lỗi đã chọn)</label>
              <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
                {dayViols.map(([k, l]) => (
                  <label key={k} className="row" style={{ gap: 6, alignItems: 'center', fontWeight: 400 }}>
                    <input type="checkbox" checked={waives.has(k)} onChange={() => toggle(k)} style={{ width: 'auto' }} /> {l}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="field">
            <label>Lý do</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Nhập lý do…" />
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button className="btn ghost" onClick={onClose}>Hủy</button>
            <button className="btn" disabled={busy} onClick={submit}>{busy ? 'Đang gửi…' : 'Gửi đề xuất'}</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function MobileAttendance() {
  const { me } = useAuth();
  const { openModal, closeModal } = useModal();
  const toast = useToast();
  const [cur, setCur] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() + 1 }; });
  const [data, setData] = useState({ days: [] });
  const [types, setTypes] = useState([]);
  const [locked, setLocked] = useState(false); // tháng đã CHỐT -> chỉ xem, ẩn form đề xuất
  const month = `${cur.y}-${pad(cur.m)}`;
  const todayS = ymd(new Date());

  const load = () => {
    attendanceMonth(me.id || me._id, month).then(setData).catch(() => setData({ days: [] }));
    attLockStatus(month).then((r) => setLocked(!!r.locked)).catch(() => setLocked(false));
  };
  useEffect(load, [month]); // eslint-disable-line
  useEffect(() => { attendanceConfig().then((c) => setTypes(c.types || [])).catch(() => {}); }, []);

  const today = data.days.find((d) => d.date === todayS);
  const byDay = {};
  data.days.forEach((d) => (byDay[Number(d.date.slice(-2))] = d));

  // Dựng lưới đủ tháng (kể cả ngày trống -> bấm xin nghỉ trước).
  const dim = new Date(cur.y, cur.m, 0).getDate();
  const lead = colOf(new Date(cur.y, cur.m - 1, 1).getDay());
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let n = 1; n <= dim; n++) {
    const date = `${month}-${pad(n)}`;
    const wd = new Date(cur.y, cur.m - 1, n).getDay();
    cells.push(byDay[n] || { date, weekday: wd, empty: true, past: date < todayS, proposal: (data.proposals || {})[date] || null });
  }

  const openDay = (d) => {
    if (!d) return;
    openModal(<ProposalSheet date={d.date} day={d} types={types} locked={locked} onClose={closeModal} onDone={() => { closeModal(); load(); }} />);
  };
  const shiftMonth = (delta) => setCur((c) => {
    let m = c.m + delta, y = c.y;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    return { y, m };
  });

  const dow = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
  const nowD = new Date();

  return (
    <div className="mbatt">
      <div className="mbatt-datehead">{dow[nowD.getDay()]}, {pad(nowD.getDate())}/{pad(nowD.getMonth() + 1)}</div>

      {/* Giờ vào / ra hôm nay */}
      <div className="card mbatt-times">
        <div className="mbhome-time">
          <div className="mbhome-time-lb">Giờ vào</div>
          <div className="mbhome-time-val" style={{ color: today?.checkIn ? 'var(--ok)' : undefined }}>{today?.checkIn || '--:--'}</div>
          <div className="small muted">{today?.checkIn ? 'Đã chấm vào' : 'Chưa chấm'}</div>
        </div>
        <div className="mbhome-time-div" />
        <div className="mbhome-time">
          <div className="mbhome-time-lb">Giờ ra</div>
          {/* Chấm 1 lần (buổi sáng) -> CHƯA có giờ ra: backend lưu checkOut = mốc MUỘN NHẤT nên
              lần chấm đầu checkOut trùng checkIn, phải dựa vào checkCount. */}
          <div className="mbhome-time-val">{(today?.checkCount || 0) >= 2 ? today.checkOut : '--:--'}</div>
          <div className="small muted">{(today?.checkCount || 0) >= 2 ? 'Đã chấm ra' : 'Chưa chấm'}</div>
        </div>
      </div>

      {/* Nút chấm công khuôn mặt tròn to */}
      <button className="mbatt-face" onClick={() => openModal(<FaceAttendanceModal onClose={closeModal} toast={toast} />)}>
        <span className="mbatt-face-circle"><i className="ti ti-face-id" /></span>
        <span className="mbatt-face-title">Chấm công online</span>
        <span className="mbatt-face-sub">Dùng khuôn mặt để chấm công</span>
      </button>

      {/* Lịch tháng */}
      <div className="card mbatt-cal">
        <div className="mbatt-cal-head">
          <button className="mbatt-cal-nav" onClick={() => shiftMonth(-1)}><i className="ti ti-chevron-left" /></button>
          <b>Tháng {cur.m}/{cur.y}</b>
          <button className="mbatt-cal-nav" onClick={() => shiftMonth(1)}><i className="ti ti-chevron-right" /></button>
        </div>
        <div className="mbatt-cal-wd">{WD.map((w) => <span key={w}>{w}</span>)}</div>
        <div className="mbatt-cal-grid">
          {cells.map((d, i) => {
            if (!d) return <span key={`b${i}`} />;
            const n = Number(d.date.slice(-2));
            const isToday = d.date === todayS;
            const ds = dots(d, todayS);
            return (
              <button key={d.date} className={'mbatt-day' + (isToday ? ' today' : '')} onClick={() => openDay(d)}>
                <span className="mbatt-day-n">{n}</span>
                <span className="mbatt-day-dots">{ds.map((c, j) => <i key={j} style={{ background: c }} />)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mbatt-hint"><i className="ti ti-info-circle" /> Chạm vào ngày để xin nghỉ hoặc giải trình</div>
    </div>
  );
}
